import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ClientService } from '../services/clientService';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { useRealtimeClients } from '../hooks/useRealtimeClients';
import { Users, Filter, Settings, Info, GripVertical } from 'lucide-react';
import { checkClientsTableSchema, showMigrationInstructions } from '../utils/clientsTableMigration';
import ClientContactsModal from './ClientContactsModal';
import ClientDetailsModal from './ClientDetailsModal';
import CustomFieldFilter from './CustomFieldFilter';
import NewClientModal from './NewClientModal';
import { Table, Button, Badge, SearchBar, Popover, FilterChip, Dropdown, Checkbox } from './ui';
import { useUserRole } from '../hooks/useUserRoleSecure';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Column Item Component
const SortableColumnItem = ({ column, checked, onChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
        backgroundColor: isDragging ? 'var(--dark-elevated)' : 'transparent',
        borderRadius: 'var(--radius-md)',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'var(--gray-400)',
          padding: '4px',
        }}
      >
        <GripVertical size={16} />
      </div>
      <Checkbox
        label={column.label}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
};

const ClientsTable = ({
  clients: externalClients = null,
  onRowClick: externalOnRowClick = null,
  onClientUpdate: externalOnClientUpdate = null
}) => {
  // Check user permissions for client management
  const { isAdmin, isOperationsManager, isOfficeAdmin } = useUserRole();
  const canManageClients = isAdmin || isOperationsManager || isOfficeAdmin;
  const [internalClients, setInternalClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Use external clients if provided (from Operations Dashboard), otherwise use internal
  const clients = externalClients || internalClients;
  const setClients = externalClients ? () => {} : setInternalClients;
  const [sortField] = useState(() => {
    const saved = localStorage.getItem('clients-sort-field');
    return saved || 'name';
  });
  const [sortDirection] = useState(() => {
    const saved = localStorage.getItem('clients-sort-direction');
    return saved || 'asc';
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [schemaValid, setSchemaValid] = useState(true);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('clients-visible-columns');
    return saved ? JSON.parse(saved) : {
      info: true,
      name: true,
      clientStatus: true,
      reason: false,
      servicesProvided: true,
      leadSource: true,
      address: true,
      clientOf: true,
      brand: false,
      trimbleFormats: false,
      contactCount: true,
      companyPhone: false,
      yearlyCost: false,
      billingStatus: false,
      platform: false,
      state: true,
      dealerName: false,
      contractorName: false,
      threeDUtilities: false,
      levelOfDetail: false,
      pricingStatus: false,
      terms: false,
      acquisitionSource: false,
      platformOther: false,
      clientsInvoicedDate: false,
      firstModelDate: false,
      firstTakeoffDate: false
    };
  });
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('clients-column-order');
    return saved ? JSON.parse(saved) : [];
  });
  const [filters, setFilters] = useState({
    clientStatus: '',
    leadSource: ''
  });
  const [customFilters, setCustomFilters] = useState([]);
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(true); // Auto-resize on by default

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Default to 20
  const [totalPages, setTotalPages] = useState(1);

  const fieldOptions = ClientService.getFieldOptions();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);

      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      setColumnOrder(newOrder);
      localStorage.setItem('clients-column-order', JSON.stringify(newOrder));
    }
  };

  useEffect(() => {
    // Only check schema and load clients if using internal mode (no external clients)
    if (!externalClients) {
      checkSchema();
    } else {
      // Using external clients, set loading to false
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalClients]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters.clientStatus, filters.leadSource, customFilters]);

  // Real-time client handlers
  const handleClientUpdate = useCallback((updatedClient) => {
    console.log('🔴 Handling real-time client update:', updatedClient);

    setClients(prev =>
      prev.map(client => {
        if (client.id === updatedClient.id) {
          // Transform the real-time update to match our frontend format
          return {
            ...client,
            clientStatus: updatedClient.client_status || 'Active Client',
            servicesProvided: updatedClient.services_provided || [],
            leadSource: updatedClient.lead_source || '',
            address: updatedClient.address || '',
            clientOf: updatedClient.client_of || '',
            brand: updatedClient.brand || [],
            trimbleFormats: updatedClient.trimble_formats || [],
            reason: updatedClient.reason || '',
            name: updatedClient.name,
            contactCount: client.contactCount // Preserve contact count from original
          };
        }
        return client;
      })
    );
  }, []);

  const handleClientInsert = useCallback((newClient) => {
    console.log('🔴 Handling real-time client insert:', newClient);
    setClients(prev => [newClient, ...prev]);
  }, []);

  const handleClientDelete = useCallback((deletedClientId) => {
    console.log('🔴 Handling real-time client delete:', deletedClientId);
    setClients(prev =>
      prev.filter(client => client.id !== deletedClientId)
    );
  }, []);

  // Set up real-time subscription for clients
  useRealtimeClients(
    handleClientUpdate,
    handleClientInsert,
    handleClientDelete,
    true
  );

  const checkSchema = async () => {
    const result = await checkClientsTableSchema();
    setSchemaValid(result.valid);

    if (result.valid) {
      loadClients();
    } else {
      setError(`Database schema issue: ${result.message}`);
      setLoading(false);
      showMigrationInstructions();
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ClientService.getAllClients();
      setClients(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleContactsClick = useCallback((e, client) => {
    e.stopPropagation();
    setSelectedClient(client);
    setShowContactsModal(true);
  }, []);

  const handleRowClick = (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleClientUpdated = async (updatedClient) => {
    if (updatedClient === null) {
      // Client was deleted
      await loadClients();
    } else {
      // Client was updated
      setClients(prev =>
        prev.map(client =>
          client.id === updatedClient.id ? updatedClient : client
        )
      );
    }
  };

  // Inline editing handlers
  const handleEditStart = useCallback((clientId, field, currentValue) => {
    if (!canManageClients) return;
    setEditingCell(`${clientId}-${field}`);
    setEditValue(currentValue || '');
  }, [canManageClients]);

  const handleEditCancel = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  const handleEditSave = useCallback(async (clientId, field) => {
    try {
      // Update local state immediately for responsiveness
      const updateData = { [field]: editValue };

      // Call the service to update the database
      const updatedClient = await ClientService.updateClient(clientId, updateData);

      // Update local state with the returned data
      if (externalClients && externalOnClientUpdate) {
        // If using external clients (Operations Dashboard), call the update callback
        externalOnClientUpdate(clientId, { [field]: editValue });
      } else {
        // Internal state update
        setClients(prev =>
          prev.map(client =>
            client.id === clientId ? { ...client, ...updatedClient } : client
          )
        );
      }

      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Error updating client:', error);
      setError(`Failed to update: ${error.message}`);
      handleEditCancel();
    }
  }, [editValue, externalClients, externalOnClientUpdate, handleEditCancel, setClients]);

  const handleKeyDown = useCallback((e, clientId, field) => {
    if (e.key === 'Enter') {
      handleEditSave(clientId, field);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  }, [handleEditSave, handleEditCancel]);

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
  };

  // Helper function to apply custom filters
  const applyCustomFilter = (client, filter) => {
    const fieldValue = client[filter.field];
    const filterValue = filter.value;

    // Handle array fields (like brand, services_provided, trimble_formats)
    if (Array.isArray(fieldValue)) {
      const arrayString = fieldValue.join(' ').toLowerCase();
      switch (filter.condition) {
        case 'equals':
          return arrayString === filterValue.toLowerCase();
        case 'contains':
          return arrayString.includes(filterValue.toLowerCase());
        case 'not_contains':
          return !arrayString.includes(filterValue.toLowerCase());
        case 'is_empty':
          return fieldValue.length === 0 || fieldValue.every(v => !v || v.trim() === '');
        case 'is_not_empty':
          return fieldValue.length > 0 && fieldValue.some(v => v && v.trim() !== '');
        case 'starts_with':
          return arrayString.startsWith(filterValue.toLowerCase());
        case 'ends_with':
          return arrayString.endsWith(filterValue.toLowerCase());
        case 'not_equals':
          return arrayString !== filterValue.toLowerCase();
        default:
          return true;
      }
    }

    // Handle string/null fields
    const stringValue = fieldValue ? fieldValue.toString().toLowerCase() : '';
    const filterValueLower = filterValue ? filterValue.toLowerCase() : '';

    switch (filter.condition) {
      case 'equals':
        return stringValue === filterValueLower;
      case 'contains':
        return stringValue.includes(filterValueLower);
      case 'not_contains':
        return !stringValue.includes(filterValueLower);
      case 'starts_with':
        return stringValue.startsWith(filterValueLower);
      case 'ends_with':
        return stringValue.endsWith(filterValueLower);
      case 'is_empty':
        return !fieldValue || fieldValue.toString().trim() === '';
      case 'is_not_empty':
        return fieldValue && fieldValue.toString().trim() !== '';
      case 'not_equals':
        return stringValue !== filterValueLower;
      default:
        return true;
    }
  };

  const filteredAndSortedClients = useMemo(() => {
    let filtered = clients;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(client =>
        client.name?.toLowerCase().includes(searchLower) ||
        client.address?.toLowerCase().includes(searchLower) ||
        client.clientOf?.toLowerCase().includes(searchLower) ||
        client.clientStatus?.toLowerCase().includes(searchLower) ||
        client.reason?.toLowerCase().includes(searchLower) ||
        (client.servicesProvided && Array.isArray(client.servicesProvided) &&
         client.servicesProvided.some(service => service?.toLowerCase().includes(searchLower))) ||
        (client.servicesProvided && typeof client.servicesProvided === 'string' &&
         client.servicesProvided.toLowerCase().includes(searchLower))
      );
    }

    // Apply standard filters
    if (filters.clientStatus) {
      filtered = filtered.filter(client => client.clientStatus === filters.clientStatus);
    }
    if (filters.leadSource) {
      filtered = filtered.filter(client => client.leadSource === filters.leadSource);
    }

    // Apply custom field filters
    if (customFilters.length > 0) {
      for (const filter of customFilters) {
        filtered = filtered.filter(client => applyCustomFilter(client, filter));
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle array fields
      if (Array.isArray(aValue)) {
        aValue = aValue.length;
      }
      if (Array.isArray(bValue)) {
        bValue = bValue.length;
      }

      // Handle numeric fields
      if (sortField === 'contactCount') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  }, [clients, searchTerm, sortField, sortDirection, filters, customFilters]);

  // Pagination: Calculate total pages and get current page data
  const displayedClients = useMemo(() => {
    const totalFiltered = filteredAndSortedClients.length;
    const calculatedTotalPages = pageSize === 999999 ? 1 : Math.ceil(totalFiltered / pageSize);

    // Update total pages if it changed
    if (calculatedTotalPages !== totalPages) {
      setTotalPages(calculatedTotalPages);
    }

    // Reset to page 1 if current page is beyond total pages
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }

    // Return all if "All" is selected
    if (pageSize === 999999) {
      return filteredAndSortedClients;
    }

    // Return current page slice
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedClients.slice(startIndex, endIndex);
  }, [filteredAndSortedClients, currentPage, pageSize, totalPages]);

  const formatServicesProvided = useCallback((services) => {
    if (!services) return [];

    let cleanedServices = [];

    // Handle array format
    if (Array.isArray(services)) {
      cleanedServices = services
        .filter(s => s && s !== 'null' && s !== null)
        .map(s => {
          let cleaned = String(s);
          // Remove outer quotes and curly braces
          cleaned = cleaned.replace(/^[{"']|[}"]$/g, '');
          // Handle JSON strings like '{"3D Models"}'
          if (cleaned.includes('"')) {
            try {
              cleaned = JSON.parse('"' + cleaned.split('"')[1] + '"');
            } catch {
              // Fallback to simple cleanup
              cleaned = cleaned.replace(/["{}]/g, '');
            }
          }
          return cleaned.trim();
        });
    }

    // Handle string format
    if (typeof services === 'string') {
      // Remove PostgreSQL array brackets if present
      let cleanStr = services.replace(/^\{|\}$/g, '');

      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(services);
        if (Array.isArray(parsed)) {
          cleanedServices = parsed
            .filter(s => s && s !== 'null' && s !== null)
            .map(s => String(s).replace(/^["']|["']$/g, '').trim());
        }
      } catch {
        // Not JSON, handle as PostgreSQL array or comma-separated
        cleanedServices = cleanStr
          .split(',')
          .map(s => s.replace(/^["'{}]|["'{}]$/g, '').trim())
          .filter(s => s && s !== 'null' && s !== '');
      }
    }

    // Final cleanup and normalization
    return cleanedServices
      .filter(s => s && s !== 'null' && s !== '')
      .map(s => {
        // Normalize known service names
        if (s.toLowerCase().includes('3d') || s.toLowerCase().includes('model')) {
          return '3D Models';
        }
        if (s.toLowerCase().includes('takeoff')) {
          return 'Takeoffs';
        }
        return s;
      });
  }, []);

  const formatTrimbleFormats = useCallback((formats) => {
    return formatServicesProvided(formats); // Same logic
  }, [formatServicesProvided]);

  // Define all columns for the Table component
  const allColumns = useMemo(() => [
    {
      id: 'info',
      label: '',
      field: 'id',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      searchable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          style={{
            padding: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--gray-400)',
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--gray-800)';
            e.currentTarget.style.color = 'var(--cta-blue)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--gray-400)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="View client details"
        >
          <Info size={20} />
        </button>
      ),
    },
    {
      id: 'name',
      label: 'Client Name',
      field: 'name',
      width: 200,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      editable: true,
      render: (value, row) => {
        const cellKey = `${row.id}-name`;
        const isEditing = editingCell === cellKey;

        if (isEditing) {
          return (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleEditSave(row.id, 'name')}
              onKeyDown={(e) => handleKeyDown(e, row.id, 'name')}
              autoFocus
              className="inline-edit-input"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%' }}
            />
          );
        }

        return (
          <div
            className={canManageClients ? 'editable-cell' : ''}
            onClick={(e) => {
              if (canManageClients) {
                e.stopPropagation();
                handleEditStart(row.id, 'name', value);
              }
            }}
            style={{ fontWeight: 'var(--font-medium)', color: 'var(--gray-200)' }}
          >
            {value || '—'}
          </div>
        );
      },
    },
    {
      id: 'clientStatus',
      label: 'Status',
      field: 'clientStatus',
      width: 140,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      editable: true,
      render: (value, row) => {
        const cellKey = `${row.id}-clientStatus`;
        const isEditing = editingCell === cellKey;

        if (isEditing) {
          return (
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleEditSave(row.id, 'clientStatus')}
              onKeyDown={(e) => handleKeyDown(e, row.id, 'clientStatus')}
              autoFocus
              className="inline-edit-select"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%' }}
            >
              <option value="">Not Set</option>
              {fieldOptions.clientStatus.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          );
        }

        return (
          <div
            className={canManageClients ? 'editable-cell' : ''}
            onClick={(e) => {
              if (canManageClients) {
                e.stopPropagation();
                handleEditStart(row.id, 'clientStatus', value);
              }
            }}
          >
            <Badge
              variant={
                value === 'Active Client' ? 'success' :
                value === 'Part Time Client' ? 'info' :
                value === 'Dropped' || value === 'Lost Client' ? 'danger' :
                'neutral'
              }
              size="sm"
            >
              {value || 'Unknown'}
            </Badge>
          </div>
        );
      },
    },
    {
      id: 'reason',
      label: 'Reason',
      field: 'reason',
      width: 150,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      editable: true,
      render: (value, row) => {
        const cellKey = `${row.id}-reason`;
        const isEditing = editingCell === cellKey;

        if (isEditing) {
          return (
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleEditSave(row.id, 'reason')}
              onKeyDown={(e) => handleKeyDown(e, row.id, 'reason')}
              autoFocus
              className="inline-edit-select"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%' }}
            >
              <option value="">Not Set</option>
              {fieldOptions.reason.map(reason => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
          );
        }

        return (
          <div
            className={canManageClients ? 'editable-cell' : ''}
            onClick={(e) => {
              if (canManageClients) {
                e.stopPropagation();
                handleEditStart(row.id, 'reason', value);
              }
            }}
          >
            {value ? (
              <Badge variant="neutral" size="sm">{value}</Badge>
            ) : (
              <span style={{ color: 'var(--gray-500)' }}>—</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'servicesProvided',
      label: 'Services Provided',
      field: 'servicesProvided',
      width: 250,
      minWidth: 150,
      sortable: false,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => {
        const services = formatServicesProvided(value);
        return services.length > 0 ? (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {services.map((service, index) => (
              <Badge key={index} variant="info" size="sm">
                {service}
              </Badge>
            ))}
          </div>
        ) : (
          '—'
        );
      },
    },
    {
      id: 'leadSource',
      label: 'Lead Source',
      field: 'leadSource',
      width: 150,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      editable: true,
      render: (value, row) => {
        const cellKey = `${row.id}-leadSource`;
        const isEditing = editingCell === cellKey;

        if (isEditing) {
          return (
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleEditSave(row.id, 'leadSource')}
              onKeyDown={(e) => handleKeyDown(e, row.id, 'leadSource')}
              autoFocus
              className="inline-edit-select"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%' }}
            >
              <option value="">Not Set</option>
              {fieldOptions.leadSource.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          );
        }

        return (
          <div
            className={canManageClients ? 'editable-cell' : ''}
            onClick={(e) => {
              if (canManageClients) {
                e.stopPropagation();
                handleEditStart(row.id, 'leadSource', value);
              }
            }}
          >
            {value || '—'}
          </div>
        );
      },
    },
    {
      id: 'address',
      label: 'Address',
      field: 'address',
      width: 250,
      minWidth: 150,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      editable: true,
      render: (value, row) => {
        const cellKey = `${row.id}-address`;
        const isEditing = editingCell === cellKey;

        if (isEditing) {
          return (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleEditSave(row.id, 'address')}
              onKeyDown={(e) => handleKeyDown(e, row.id, 'address')}
              autoFocus
              className="inline-edit-input"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%' }}
            />
          );
        }

        return (
          <div
            className={canManageClients ? 'editable-cell' : ''}
            onClick={(e) => {
              if (canManageClients) {
                e.stopPropagation();
                handleEditStart(row.id, 'address', value);
              }
            }}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value || '—'}
          </div>
        );
      },
    },
    {
      id: 'clientOf',
      label: 'Client Of',
      field: 'clientOf',
      width: 180,
      minWidth: 120,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      editable: true,
      render: (value, row) => {
        const cellKey = `${row.id}-clientOf`;
        const isEditing = editingCell === cellKey;

        if (isEditing) {
          return (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleEditSave(row.id, 'clientOf')}
              onKeyDown={(e) => handleKeyDown(e, row.id, 'clientOf')}
              autoFocus
              className="inline-edit-input"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%' }}
            />
          );
        }

        return (
          <div
            className={canManageClients ? 'editable-cell' : ''}
            onClick={(e) => {
              if (canManageClients) {
                e.stopPropagation();
                handleEditStart(row.id, 'clientOf', value);
              }
            }}
          >
            {value || '—'}
          </div>
        );
      },
    },
    {
      id: 'brand',
      label: 'Brand',
      field: 'brand',
      width: 150,
      minWidth: 100,
      sortable: false,
      resizable: true,
      contentType: 'badge',
      render: (value) => {
        const brands = formatServicesProvided(value);
        return brands.length > 0 ? (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {brands.map((brand, index) => (
              <Badge key={index} variant="neutral" size="sm">
                {brand}
              </Badge>
            ))}
          </div>
        ) : (
          '—'
        );
      },
    },
    {
      id: 'trimbleFormats',
      label: 'Trimble Formats',
      field: 'trimbleFormats',
      width: 200,
      minWidth: 150,
      sortable: false,
      resizable: true,
      contentType: 'badge',
      render: (value) => {
        const formats = formatTrimbleFormats(value);
        return formats.length > 0 ? (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {formats.map((format, index) => (
              <Badge key={index} variant="neutral" size="sm">
                {format}
              </Badge>
            ))}
          </div>
        ) : (
          '—'
        );
      },
    },
    {
      id: 'contactCount',
      label: 'Contacts',
      field: 'contactCount',
      width: 100,
      minWidth: 80,
      sortable: true,
      resizable: true,
      align: 'center',
      contentType: 'icon',
      render: (value, row) => (
        <button
          className="contact-count-btn"
          onClick={(e) => handleContactsClick(e, row)}
          title="View contacts"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: 'var(--dark-elevated)',
            border: '1px solid var(--gray-600)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--gray-200)',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--cta-blue)';
            e.currentTarget.style.color = 'var(--cta-blue)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--gray-600)';
            e.currentTarget.style.color = 'var(--gray-200)';
          }}
        >
          <Users size={16} />
          <span>{value || 0}</span>
        </button>
      ),
    },
    {
      id: 'companyPhone',
      label: 'Phone',
      field: 'companyPhone',
      width: 150,
      minWidth: 120,
      sortable: true,
      resizable: true,
      contentType: 'text',
      editable: true,
      render: (value, row) => {
        const cellKey = `${row.id}-companyPhone`;
        const isEditing = editingCell === cellKey;

        if (isEditing) {
          return (
            <input
              type="tel"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleEditSave(row.id, 'companyPhone')}
              onKeyDown={(e) => handleKeyDown(e, row.id, 'companyPhone')}
              autoFocus
              className="inline-edit-input"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%' }}
            />
          );
        }

        return (
          <div
            className={canManageClients ? 'editable-cell' : ''}
            onClick={(e) => {
              if (canManageClients) {
                e.stopPropagation();
                handleEditStart(row.id, 'companyPhone', value);
              }
            }}
          >
            {value || '—'}
          </div>
        );
      },
    },
    {
      id: 'yearlyCost',
      label: 'Yearly Cost',
      field: 'yearlyCost',
      width: 120,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) =>
        value ? `$${value.toLocaleString()}` : '—',
    },
    {
      id: 'billingStatus',
      label: 'Billing Status',
      field: 'billingStatus',
      width: 140,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) =>
        value ? (
          <Badge variant="neutral" size="sm">
            {value}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'platform',
      label: 'Platform',
      field: 'platform',
      width: 120,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'state',
      label: 'State',
      field: 'state',
      width: 100,
      minWidth: 80,
      sortable: true,
      resizable: true,
      contentType: 'text',
      editable: true,
      render: (value, row) => {
        const cellKey = `${row.id}-state`;
        const isEditing = editingCell === cellKey;

        if (isEditing) {
          return (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleEditSave(row.id, 'state')}
              onKeyDown={(e) => handleKeyDown(e, row.id, 'state')}
              autoFocus
              className="inline-edit-input"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%' }}
            />
          );
        }

        return (
          <div
            className={canManageClients ? 'editable-cell' : ''}
            onClick={(e) => {
              if (canManageClients) {
                e.stopPropagation();
                handleEditStart(row.id, 'state', value);
              }
            }}
          >
            {value || '—'}
          </div>
        );
      },
    },
    {
      id: 'dealerName',
      label: 'Dealer Name',
      field: 'dealerName',
      width: 150,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'contractorName',
      label: 'Contractor Name',
      field: 'contractorName',
      width: 150,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'threeDUtilities',
      label: '3D Utilities',
      field: 'threeDUtilities',
      width: 140,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'levelOfDetail',
      label: 'Level of Detail',
      field: 'levelOfDetail',
      width: 140,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'pricingStatus',
      label: 'Pricing Status',
      field: 'pricingStatus',
      width: 130,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) =>
        value ? (
          <Badge variant="neutral" size="sm">
            {value}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'terms',
      label: 'Terms',
      field: 'terms',
      width: 100,
      minWidth: 80,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'acquisitionSource',
      label: 'Acquisition Source',
      field: 'acquisitionSource',
      width: 160,
      minWidth: 120,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'platformOther',
      label: 'Platform Other',
      field: 'platformOther',
      width: 140,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'clientsInvoicedDate',
      label: 'Invoiced Date',
      field: 'clientsInvoicedDate',
      width: 150,
      minWidth: 120,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) =>
        value ? new Date(value).toLocaleDateString() : '—',
    },
    {
      id: 'firstModelDate',
      label: 'First Model Date',
      field: 'firstModelDate',
      width: 130,
      minWidth: 120,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) =>
        value ? new Date(value).toLocaleDateString() : '—',
    },
    {
      id: 'firstTakeoffDate',
      label: 'First Takeoff Date',
      field: 'firstTakeoffDate',
      width: 150,
      minWidth: 120,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) =>
        value ? new Date(value).toLocaleDateString() : '—',
    },
  ], [handleContactsClick, formatTrimbleFormats, formatServicesProvided, editingCell, editValue, canManageClients, handleEditStart, handleEditSave, handleKeyDown, fieldOptions]);

  // Initialize column order if empty
  useEffect(() => {
    if (columnOrder.length === 0 && allColumns.length > 0) {
      const defaultOrder = allColumns.map(col => col.id);
      setColumnOrder(defaultOrder);
      localStorage.setItem('clients-column-order', JSON.stringify(defaultOrder));
    }
  }, [allColumns, columnOrder.length]);

  // Get visible columns for table display with custom ordering
  const columns = useMemo(() => {
    // If no custom order, use default allColumns order
    if (columnOrder.length === 0) {
      return allColumns.filter(col => visibleColumns[col.id]);
    }

    // Apply custom order
    const orderedColumns = columnOrder
      .map(colId => allColumns.find(col => col.id === colId))
      .filter(col => col && visibleColumns[col.id]);

    return orderedColumns;
  }, [allColumns, columnOrder, visibleColumns]);

  if (loading) {
    return <LoadingSpinner message="Loading clients..." />;
  }

  // Show migration needed message
  if (!schemaValid) {
    return (
      <div className="clients-page">
        <div className="migration-needed">
          <h2>Database Migration Required</h2>
          <p>The clients table needs to be updated to support new features.</p>
          <div className="migration-details">
            <h3>Missing columns detected in the database</h3>
            <p>Please run the following SQL in your Supabase SQL Editor:</p>
            <pre className="migration-sql">
{`ALTER TABLE clients
ADD COLUMN IF NOT EXISTS client_status VARCHAR(20) DEFAULT 'active';
ADD COLUMN IF NOT EXISTS services_provided TEXT;
ADD COLUMN IF NOT EXISTS lead_source VARCHAR(100);
ADD COLUMN IF NOT EXISTS address TEXT;
ADD COLUMN IF NOT EXISTS client_of VARCHAR(255);`}
            </pre>
            <p>Check the browser console for detailed instructions.</p>
          </div>
          <button onClick={() => window.location.reload()} className="retry-btn">
            Retry After Migration
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ErrorMessage
            message={error}
            onRetry={loadClients}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            Clients
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {filteredAndSortedClients.length} total clients
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={() => setShowNewClientModal(true)}
          disabled={!canManageClients}
          title={!canManageClients ? "You don't have permission to create clients" : "Create a new client"}
        >
          + New Client
        </Button>
      </div>

      {/* Active Filter Chips */}
      {(filters.clientStatus || filters.leadSource || customFilters.length > 0) && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-4)',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--gray-400)',
            fontWeight: 'var(--font-medium)'
          }}>
            Active filters:
          </span>
          {filters.clientStatus && (
            <FilterChip
              label={`Status: ${filters.clientStatus}`}
              onRemove={() => setFilters({ ...filters, clientStatus: '' })}
            />
          )}
          {filters.leadSource && (
            <FilterChip
              label={`Lead Source: ${filters.leadSource}`}
              onRemove={() => setFilters({ ...filters, leadSource: '' })}
            />
          )}
          {customFilters.map((filter, index) => (
            <FilterChip
              key={index}
              label={`${filter.field}: ${filter.condition} ${filter.value}`}
              onRemove={() => {
                setCustomFilters(customFilters.filter((_, i) => i !== index));
              }}
            />
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilters({ clientStatus: '', leadSource: '' });
              setCustomFilters([]);
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Table with integrated toolbar */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '85vh',
      }}>
        {/* Search Bar and Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search all clients..."
              size="sm"
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowFilters(!showFilters);
                    setShowColumnSettings(false);
                  }}
                >
                  <Filter size={16} />
                  Filters
                </Button>
              }
              position="bottom-right"
              width="600px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <h3 style={{
                  margin: 0,
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)'
                }}>
                  Filter Clients
                </h3>

                {/* Status Filter */}
                <Dropdown
                  label="Status"
                  value={filters.clientStatus}
                  options={[
                    { value: '', label: 'All statuses' },
                    ...fieldOptions.clientStatus.map(status => ({ value: status, label: status }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, clientStatus: value })}
                  placeholder="All statuses"
                  size="md"
                />

                {/* Lead Source Filter */}
                <Dropdown
                  label="Lead Source"
                  value={filters.leadSource}
                  options={[
                    { value: '', label: 'All sources' },
                    ...fieldOptions.leadSource.map(source => ({ value: source, label: source }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, leadSource: value })}
                  placeholder="All sources"
                  size="md"
                />

                {/* Advanced Filters */}
                <div>
                  <h4 style={{
                    margin: 0,
                    marginBottom: 'var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-300)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Advanced Filters
                  </h4>
                  <CustomFieldFilter
                    currentFilters={customFilters}
                    onFiltersChange={setCustomFilters}
                  />
                </div>

                {/* Footer Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: 'var(--space-3)',
                  borderTop: '1px solid var(--gray-700)'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFilters({ clientStatus: '', leadSource: '' });
                      setCustomFilters([]);
                    }}
                  >
                    Clear all
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </Popover>

            <Popover
              isOpen={showColumnSettings}
              onClose={() => setShowColumnSettings(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowColumnSettings(!showColumnSettings);
                    setShowFilters(false);
                  }}
                >
                  <Settings size={16} />
                  Columns
                </Button>
              }
              position="bottom-right"
              width="520px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Header with count */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)'
                  }}>
                    Toggle Columns
                  </h3>
                  <span style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--gray-400)',
                    backgroundColor: 'var(--dark-elevated)',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    fontWeight: 'var(--font-medium)'
                  }}>
                    {Object.values(visibleColumns).filter(Boolean).length} / {Object.keys(visibleColumns).length} visible
                  </span>
                </div>

                {/* Quick Actions */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  paddingBottom: 'var(--space-3)',
                  borderBottom: '1px solid var(--gray-700)',
                  flexWrap: 'wrap'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allVisible = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allVisible[key] = true;
                      });
                      setVisibleColumns(allVisible);
                      localStorage.setItem('clients-visible-columns', JSON.stringify(allVisible));
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allHidden = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allHidden[key] = false;
                      });
                      setVisibleColumns(allHidden);
                      localStorage.setItem('clients-visible-columns', JSON.stringify(allHidden));
                    }}
                  >
                    Deselect All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const defaultOrder = allColumns.map(col => col.id);
                      setColumnOrder(defaultOrder);
                      localStorage.setItem('clients-column-order', JSON.stringify(defaultOrder));
                    }}
                  >
                    Reset Order
                  </Button>
                </div>

                {/* Column Checkboxes with Drag & Drop */}
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: 'var(--space-2)'
                }}>
                  {/* Visible Columns (Checked) */}
                  <div style={{
                    marginBottom: 'var(--space-3)'
                  }}>
                    <div style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-400)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 'var(--space-2)',
                      paddingLeft: 'var(--space-2)'
                    }}>
                      Visible Columns
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={columnOrder.filter(id => id !== 'info' && visibleColumns[id])}
                        strategy={verticalListSortingStrategy}
                      >
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-1)'
                        }}>
                          {columnOrder
                            .filter(id => id !== 'info' && visibleColumns[id])
                            .map(colId => {
                              const col = allColumns.find(c => c.id === colId);
                              if (!col) return null;
                              return (
                                <SortableColumnItem
                                  key={col.id}
                                  column={col}
                                  checked={visibleColumns[col.id]}
                                  onChange={(e) => {
                                    const newVisibleColumns = {
                                      ...visibleColumns,
                                      [col.id]: e.target.checked
                                    };
                                    setVisibleColumns(newVisibleColumns);
                                    localStorage.setItem('clients-visible-columns', JSON.stringify(newVisibleColumns));
                                  }}
                                />
                              );
                            })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>

                  {/* Hidden Columns (Unchecked) */}
                  {columnOrder.filter(id => id !== 'info' && !visibleColumns[id]).length > 0 && (
                    <div style={{
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <div style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-400)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 'var(--space-2)',
                        paddingLeft: 'var(--space-2)'
                      }}>
                        Hidden Columns
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={columnOrder.filter(id => id !== 'info' && !visibleColumns[id])}
                          strategy={verticalListSortingStrategy}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-1)'
                          }}>
                            {columnOrder
                              .filter(id => id !== 'info' && !visibleColumns[id])
                              .map(colId => {
                                const col = allColumns.find(c => c.id === colId);
                                if (!col) return null;
                                return (
                                  <SortableColumnItem
                                    key={col.id}
                                    column={col}
                                    checked={visibleColumns[col.id]}
                                    onChange={(e) => {
                                      const newVisibleColumns = {
                                        ...visibleColumns,
                                        [col.id]: e.target.checked
                                      };
                                      setVisibleColumns(newVisibleColumns);
                                      localStorage.setItem('clients-visible-columns', JSON.stringify(newVisibleColumns));
                                    }}
                                  />
                                );
                              })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                </div>
              </div>
            </Popover>

            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div style={{
          backgroundColor: 'var(--dark-surface)',
          overflowX: 'auto',
          overflowY: 'auto',
          flex: 1,
          minWidth: 0,
        }}>
          <Table
          data={displayedClients}
          columns={columns}
          sortable={true}
          searchable={false}
          resizable={true}
          paginated={false} // We're handling pagination manually with custom controls
          loading={loading}
          storageKey="clients-table"
          autoResizeOnDataChange={autoResizeEnabled}
          showAutoSizeButton={false}
          onManualResize={handleManualResize}
          emptyMessage={
            searchTerm || Object.values(filters).some(f => f) || customFilters.length > 0
              ? 'No clients found matching your search or filters'
              : 'No clients found. Click "+ New Client" to add your first client.'
          }
          onRowClick={handleRowClick}
          variant="dense"
          stickyHeader={true}
          style={{ border: 'none', borderRadius: '0', overflow: 'visible' }}
        />
        </div>
        {/* End Scrollable Table Container */}

        {/* Frozen Pagination */}
        {filteredAndSortedClients.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderTop: '1px solid var(--gray-600)',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                {pageSize === 999999 ? (
                  `Showing all ${filteredAndSortedClients.length} client${filteredAndSortedClients.length !== 1 ? 's' : ''}`
                ) : (
                  `Page ${currentPage} of ${totalPages} (${filteredAndSortedClients.length} client${filteredAndSortedClients.length !== 1 ? 's' : ''})`
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                  Show:
                </span>
                <Button
                  variant={pageSize === 20 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(20)}
                >
                  20
                </Button>
                <Button
                  variant={pageSize === 50 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(50)}
                >
                  50
                </Button>
                <Button
                  variant={pageSize === 999999 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(999999)}
                >
                  All
                </Button>
              </div>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </Button>
                    );
                  }

                  return pages;
                })()}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Contacts Modal */}
      <ClientContactsModal
        client={selectedClient}
        isOpen={showContactsModal}
        onClose={() => {
          setShowContactsModal(false);
          setSelectedClient(null);
        }}
      />

      {/* Client Details Modal */}
      {showDetailsModal && (
        <ClientDetailsModal
          client={selectedClient}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedClient(null);
          }}
          onClientUpdated={handleClientUpdated}
          canManageClients={canManageClients}
        />
      )}

      {/* New Client Modal */}
      {showNewClientModal && (
        <NewClientModal
          isOpen={showNewClientModal}
          onClose={() => setShowNewClientModal(false)}
          onClientCreated={(newClient) => {
            loadClients();
            setShowNewClientModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ClientsTable;
